import React, { FormEvent, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Footer from '../../../components/Footer';
import Header from '../../../components/Header';
import { CONTRACT_TERMS_TEXT } from '../../../contractTerms';
import { lookupContract, signContract, selectPlan, type PublicContract, type PlanType } from './contractApi';
import SignaturePadModal from './SignaturePadModal';
import PhoneNumberInput from '../../../components/PhoneNumberInput';
import './Contract.scss';

const CONTRACT_TYPE_LABELS: Record<string, string> = {
  web: '웹 플랫폼 제작',
  app: '모바일 앱 제작',
  maintenance: '유지보수 · 고도화',
  consulting: '기획 · 컨설팅',
};

const STEPS = [
  { id: 1, label: '신청 내역 조회' },
  { id: 2, label: '약관 확인' },
  { id: 3, label: '전자서명' },
] as const;

interface LookupFormState {
  customerName: string;
  phone: string;
}

interface AgreeFormState {
  termsAgreed: boolean;
  privacyAgreed: boolean;
  signatureImage: string;
}

type LookupErrors = Partial<Record<keyof LookupFormState, string>>;
type AgreeErrors = Partial<Record<keyof AgreeFormState, string>>;

const PHONE_PATTERN = /^[0-9+\-()\s]{8,20}$/;

const INITIAL_LOOKUP: LookupFormState = {
  customerName: '',
  phone: '',
};

const INITIAL_AGREE: AgreeFormState = {
  termsAgreed: false,
  privacyAgreed: false,
  signatureImage: '',
};

function getContractTypeLabel(type: string) {
  return CONTRACT_TYPE_LABELS[type] || type || '-';
}

function formatSignedAt(value?: string | null) {
  if (!value) return '-';
  const normalized = String(value).replace(' ', 'T');
  const date = new Date(normalized);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString('ko-KR', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function getSignatureImageSrc(value?: string | null) {
  const text = String(value || '').trim();
  if (!text.startsWith('data:image/')) return null;
  return text;
}

function getSignatureText(value?: string | null) {
  const text = String(value || '').trim();
  return text || null;
}

// 2026-09-27 요금제 개편으로 새 신청은 월 요금(구독) 하나뿐 - 구매형은 옛 계약 표시용으로만 남겨둔다
// (서버 selectPlan도 항상 subscription으로 저장한다, server/routers/common/pricing.js 참고).
const PLAN_OPTIONS: { id: PlanType; title: string; desc: string; tag: string }[] = [
  {
    id: 'subscription',
    title: '구독형',
    desc: '매월 정기결제로 이용하는 요금제입니다.',
    tag: '월 정기결제',
  },
  {
    id: 'purchase',
    title: '구매형',
    desc: '일시불로 구매 후 잔금을 결제하는 요금제입니다.',
    tag: '단건결제',
  },
];

type ContractCategory = 'deposit' | 'balance';

const CONTRACT_CATEGORIES: { id: ContractCategory; title: string; desc: string; tag: string }[] = [
  {
    id: 'deposit',
    title: '계약금 결제',
    desc: '문의 및 신청 후, 가계약 성격의 계약금을 결제합니다.',
    tag: '문의 · 신청 후',
  },
  {
    id: 'balance',
    title: '잔금 · 정기결제',
    desc: '제작 및 확인 후, 잔금 또는 정기결제를 진행합니다.',
    tag: '제작 · 확인 후',
  },
];

const Contract: React.FC = () => {
  // TODO: 계약 유형 선택 화면(2버튼) 비활성화 — 임시로 '계약금'으로 고정
  const [contractCategory, setContractCategory] = useState<ContractCategory | null>('deposit');
  const [step, setStep] = useState(1);
  const [lookup, setLookup] = useState<LookupFormState>(INITIAL_LOOKUP);
  const [agree, setAgree] = useState<AgreeFormState>(INITIAL_AGREE);
  const [contract, setContract] = useState<PublicContract | null>(null);
  const [lookupErrors, setLookupErrors] = useState<LookupErrors>({});
  const [agreeErrors, setAgreeErrors] = useState<AgreeErrors>({});
  const [isLookingUp, setIsLookingUp] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSignatureModalOpen, setIsSignatureModalOpen] = useState(false);
  const [isSelectingPlan, setIsSelectingPlan] = useState(false);
  const [planError, setPlanError] = useState('');

  useEffect(() => {
    const nodes = document.querySelectorAll('.contract .reveal');
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('in');
            io.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12 }
    );

    nodes.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);

  const updateLookup = <K extends keyof LookupFormState>(key: K, value: LookupFormState[K]) => {
    setLookup((prev) => ({ ...prev, [key]: value }));
    setLookupErrors((prev) => ({ ...prev, [key]: undefined }));
  };

  const updateAgree = <K extends keyof AgreeFormState>(key: K, value: AgreeFormState[K]) => {
    setAgree((prev) => ({ ...prev, [key]: value }));
    setAgreeErrors((prev) => ({ ...prev, [key]: undefined }));
  };

  const signerName = (contract?.representative || contract?.customer_name || '').trim();
  const isSignedView = contract?.status === 'signed' || contract?.status === 'completed';
  const needsMvpConfirm =
    contract != null && !['mvp_confirmed', 'signed', 'completed'].includes(contract.status);
  const needsPlanSelection = contract?.status === 'mvp_confirmed' && !contract.plan_type;
  const signatureImageSrc = getSignatureImageSrc(contract?.signature_image);
  const signatureText = getSignatureText(contract?.signature_name);
  const contractTermsText = contract?.terms_text || CONTRACT_TERMS_TEXT;

  const validateLookup = (): LookupErrors => {
    const nextErrors: LookupErrors = {};
    if (!lookup.customerName.trim()) nextErrors.customerName = '고객명을 입력해 주세요.';
    if (!lookup.phone.trim()) {
      nextErrors.phone = '전화번호를 입력해 주세요.';
    } else if (!PHONE_PATTERN.test(lookup.phone.trim())) {
      nextErrors.phone = '올바른 연락처 형식을 입력해 주세요.';
    }
    return nextErrors;
  };

  const validateAgreeStep = (): AgreeErrors => {
    const nextErrors: AgreeErrors = {};
    if (!agree.termsAgreed) nextErrors.termsAgreed = '계약 약관에 동의해 주세요.';
    if (!agree.privacyAgreed) nextErrors.privacyAgreed = '개인정보 수집 및 이용에 동의해 주세요.';
    return nextErrors;
  };

  const validateSignStep = (): AgreeErrors => {
    const nextErrors: AgreeErrors = {};
    if (!agree.signatureImage) {
      nextErrors.signatureImage = '서명을 입력해 주세요.';
    }
    return nextErrors;
  };

  const handleLookup = async () => {
    const validationErrors = validateLookup();
    if (Object.keys(validationErrors).length > 0) {
      setLookupErrors(validationErrors);
      return;
    }

    setIsLookingUp(true);
    setLookupErrors({});
    try {
      const found = await lookupContract(lookup.customerName.trim(), lookup.phone.trim());
      setContract(found);
      setAgree(INITIAL_AGREE);
      setAgreeErrors({});
      setStep(2);
    } catch (error) {
      setContract(null);
      setLookupErrors({
        customerName: error instanceof Error ? error.message : '계약 조회에 실패했습니다.',
      });
    } finally {
      setIsLookingUp(false);
    }
  };

  const goNext = () => {
    if (step === 1) {
      void handleLookup();
      return;
    }

    if (step === 2) {
      if (isSignedView || needsMvpConfirm || needsPlanSelection) return;

      const validationErrors = validateAgreeStep();
      if (Object.keys(validationErrors).length > 0) {
        setAgreeErrors(validationErrors);
        return;
      }
      setAgreeErrors({});
      setStep(3);
    }
  };

  const goPrev = () => {
    setLookupErrors({});
    setAgreeErrors({});
    setIsSignatureModalOpen(false);
    if (step === 1) {
      setContractCategory(null);
      return;
    }
    if (step === 2) {
      setContract(null);
      setAgree(INITIAL_AGREE);
    }
    if (step === 3) {
      setAgree((prev) => ({ ...prev, signatureImage: '' }));
    }
    setStep((prev) => Math.max(prev - 1, 1));
  };

  const handleSelectPlan = async (planType: PlanType) => {
    if (!contract) return;
    setIsSelectingPlan(true);
    setPlanError('');
    try {
      const updated = await selectPlan(contract.id, {
        customer_name: lookup.customerName.trim(),
        phone: lookup.phone.trim(),
        planType,
      });
      setContract(updated);
    } catch (error) {
      setPlanError(error instanceof Error ? error.message : '요금제 선택에 실패했습니다.');
    } finally {
      setIsSelectingPlan(false);
    }
  };

  const handleSignatureConfirm = (dataUrl: string) => {
    updateAgree('signatureImage', dataUrl);
    setIsSignatureModalOpen(false);
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!contract) return;

    const validationErrors = { ...validateAgreeStep(), ...validateSignStep() };
    if (Object.keys(validationErrors).length > 0) {
      setAgreeErrors(validationErrors);
      if (validationErrors.signatureImage) {
        setIsSignatureModalOpen(true);
      }
      return;
    }

    setIsSubmitting(true);
    setAgreeErrors({});

    try {
      await signContract(contract.id, {
        customer_name: lookup.customerName.trim(),
        phone: lookup.phone.trim(),
        terms_agreed: agree.termsAgreed,
        privacy_agreed: agree.privacyAgreed,
        signature_name: signerName,
        signature_image: agree.signatureImage,
      });
      setIsSubmitted(true);
      window.scrollTo(0, 0);
    } catch (error) {
      console.error('Failed to submit contract:', error);
      setAgreeErrors({
        signatureImage:
          error instanceof Error
            ? error.message
            : '계약 접수 중 오류가 발생했습니다. 잠시 후 다시 시도해 주세요.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  /* 계약 유형 선택 화면(2버튼) 임시 비활성화 — 위 contractCategory 기본값으로 대체
  if (!contractCategory) {
    return (
      <div className="contract">
        <Header />
        <section className="page-header">
          <div className="container reveal in">
            <div className="breadcrumb">
              <Link to="/">Home</Link> <span>· 서비스신청 · 계약</span>
            </div>
            <div className="page-header-grid">
              <div>
                <h1 className="page-title">
                  어떤 <span className="em">계약</span>을
                  <br />
                  진행하시나요?
                </h1>
                <p className="page-sub">
                  진행 단계에 따라 계약 유형을 선택해 주세요.
                </p>
              </div>
              <div className="page-meta">Online Contract</div>
            </div>
          </div>
        </section>

        <section className="contract-section">
          <div className="container">
            <div className="contract-category-grid">
              {CONTRACT_CATEGORIES.map((cat) => (
                <button
                  key={cat.id}
                  type="button"
                  className="contract-category-card"
                  onClick={() => setContractCategory(cat.id)}
                >
                  <span className="contract-category-card__tag">{cat.tag}</span>
                  <h2 className="contract-category-card__title">{cat.title}</h2>
                  <p className="contract-category-card__desc">{cat.desc}</p>
                  <span className="contract-category-card__arrow">→</span>
                </button>
              ))}
            </div>
          </div>
        </section>
        <Footer />
      </div>
    );
  }
  */

  if (isSubmitted) {
    return (
      <div className="contract">
        <Header />
        <section className="contract-success">
          <div className="container contract-success__inner">
            <div className="contract-success__icon" aria-hidden="true" />
            <h1 className="contract-success__title">온라인 계약이 완료되었습니다.</h1>
            <p className="contract-success__desc">
              전자서명이 정상적으로 접수되었습니다. 이어서 최종결제를 진행해 주세요.
            </p>
            <button
              type="button"
              className="contract-success__button"
              onClick={() => {
                window.location.href = '/payment';
              }}
            >
              최종결제 진행하기 →
            </button>
          </div>
        </section>
        <Footer />
      </div>
    );
  }

  return (
    <div className="contract">
      <Header />

      <section className="page-header">
        <div className="container reveal">
          <div className="breadcrumb">
            <Link to="/">Home</Link> <span>· 서비스신청 · 계약</span>
          </div>
          <div className="page-header-grid">
            <div>
              <h1 className="page-title">
                온라인으로 간편하게,
                <br />
                <span className="em">계약</span>을 완료하세요.
              </h1>
              <p className="page-sub">
                안내받으신 고객명과 전화번호를 입력하면 계약 내용을 확인할 수 있습니다. 약관
                동의와 전자서명만 진행해 주세요.
              </p>
            </div>
            <div className="page-meta">Online Contract</div>
          </div>
        </div>
      </section>

      <section className="contract-section">
        <div className="container">
          <ol className="contract-steps" aria-label="계약 진행 단계">
            {STEPS.map((item) => {
              const isActive = step === item.id;
              const isDone = step > item.id;
              return (
                <li
                  key={item.id}
                  className={`contract-steps__item${isActive ? ' contract-steps__item--active' : ''}${
                    isDone ? ' contract-steps__item--done' : ''
                  }`}
                >
                  <span className="contract-steps__num" aria-hidden="true">
                    {isDone ? '✓' : item.id}
                  </span>
                  <span className="contract-steps__label">{item.label}</span>
                </li>
              );
            })}
          </ol>

          <form className="contract-form" onSubmit={handleSubmit} noValidate>
            {step === 1 && (
              <div className="contract-panel">
                <h2 className="contract-panel__title">신청 내역 조회</h2>
                <p className="contract-panel__desc">
                  담당자가 안내한 고객명과 전화번호를 입력해 계약을 불러와 주세요.
                </p>

                <div className="contract-form__grid">
                  <div className="contract-field contract-field--inline">
                    <label className="contract-field__label" htmlFor="contract-customer">
                      고객명 <span className="contract-field__required">*</span>
                    </label>
                    <input
                      id="contract-customer"
                      className={`contract-field__input ${
                        lookupErrors.customerName ? 'contract-field__input--error' : ''
                      }`}
                      type="text"
                      value={lookup.customerName}
                      onChange={(e) => updateLookup('customerName', e.target.value)}
                      placeholder="홍길동"
                      autoComplete="name"
                    />
                    {lookupErrors.customerName && (
                      <p className="contract-field__error">{lookupErrors.customerName}</p>
                    )}
                  </div>

                  <div className="contract-field contract-field--inline">
                    <label className="contract-field__label" htmlFor="contract-phone">
                      전화번호 <span className="contract-field__required">*</span>
                    </label>
                    <PhoneNumberInput
                      id="contract-phone"
                      className={`contract-field__input ${
                        lookupErrors.phone ? 'contract-field__input--error' : ''
                      }`}
                      value={lookup.phone}
                      onChange={(phone) => updateLookup('phone', phone)}
                      autoComplete="tel"
                    />
                    {lookupErrors.phone && (
                      <p className="contract-field__error">{lookupErrors.phone}</p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {step === 2 && contract && needsMvpConfirm && (
              <div className="contract-panel">
                <h2 className="contract-panel__title">아직 계약서 작성 단계가 아닙니다</h2>
                <p className="contract-panel__desc">
                  MVP 제작 및 시안 확인이 먼저 완료되어야 계약서를 작성할 수 있습니다.
                </p>
                <Link to="/mvp-confirm" className="contract-actions__next" style={{ display: 'inline-flex' }}>
                  MVP 확인하러 가기 →
                </Link>
              </div>
            )}

            {step === 2 && contract && !needsMvpConfirm && needsPlanSelection && (
              <div className="contract-panel">
                <h2 className="contract-panel__title">요금제 선택</h2>
                <p className="contract-panel__desc">
                  선택하신 성도 규모와 앱 기준의 월 정기결제로 진행됩니다.
                </p>

                <div className="contract-category-grid">
                  {PLAN_OPTIONS.filter((plan) => plan.id === 'subscription').map(
                    (plan) => (
                      <button
                        key={plan.id}
                        type="button"
                        className="contract-category-card"
                        onClick={() => handleSelectPlan(plan.id)}
                        disabled={isSelectingPlan}
                      >
                        <span className="contract-category-card__tag">{plan.tag}</span>
                        <h2 className="contract-category-card__title">{plan.title}</h2>
                        <p className="contract-category-card__desc">{plan.desc}</p>
                        <span className="contract-category-card__arrow">→</span>
                      </button>
                    ),
                  )}
                </div>
                {planError && <p className="contract-field__error">{planError}</p>}
              </div>
            )}

            {step === 2 && contract && !needsMvpConfirm && !needsPlanSelection && (
              <div className="contract-panel">
                <h2 className="contract-panel__title">
                  {isSignedView ? '서명 완료 계약' : '계약 내용 확인'}
                </h2>
                <p className="contract-panel__desc">
                  {isSignedView
                    ? '이미 서명이 완료된 계약입니다. 아래에서 계약 내용과 서명 정보를 확인할 수 있습니다.'
                    : '아래 계약 내용과 약관을 확인하신 후 동의해 주세요.'}
                </p>

                {isSignedView && (
                  <div className="contract-signed-notice" role="status">
                    <span className="contract-signed-notice__badge">서명 완료</span>
                    <span className="contract-signed-notice__text">
                      {formatSignedAt(contract.signed_at)}에 전자서명이 완료되었습니다.
                    </span>
                  </div>
                )}

                <div className="contract-field contract-field--full">
                  <span className="contract-field__label">계약 개요</span>
                  <div className="contract-summary-card">
                    {contract.contract_name && (
                      <div className="contract-summary-card__row">
                        <span className="contract-summary-card__label">계약명</span>
                        <span className="contract-summary-card__value">
                          {contract.contract_name}
                        </span>
                      </div>
                    )}
                    {contract.product_name && (
                      <div className="contract-summary-card__row">
                        <span className="contract-summary-card__label">상품명</span>
                        <span className="contract-summary-card__value">
                          {contract.product_name}
                        </span>
                      </div>
                    )}
                    <div className="contract-summary-card__row">
                      <span className="contract-summary-card__label">고객명</span>
                      <span className="contract-summary-card__value">
                        {contract.customer_name || '-'}
                      </span>
                    </div>
                    <div className="contract-summary-card__row">
                      <span className="contract-summary-card__label">연락처</span>
                      <span className="contract-summary-card__value">
                        {contract.phone || '-'}
                      </span>
                    </div>
                    <div className="contract-summary-card__row">
                      <span className="contract-summary-card__label">계약 유형</span>
                      <span className="contract-summary-card__value">
                        {getContractTypeLabel(String(contract.contract_type))}
                      </span>
                    </div>
                  </div>
                </div>

                {contract.special_notes && (
                  <div className="contract-field contract-field--full">
                    <span className="contract-field__label">특이사항</span>
                    <div className="contract-readonly">{contract.special_notes}</div>
                  </div>
                )}

                {(contract.build_cost ||
                  contract.build_period ||
                  contract.maintenance_fee ||
                  contract.maintenance_period) && (
                  <div className="contract-field contract-field--full">
                    <span className="contract-field__label">계약 비용</span>
                    <div className="contract-summary-card">
                      {contract.build_cost && (
                        <div className="contract-summary-card__row">
                          <span className="contract-summary-card__label">구축비</span>
                          <span className="contract-summary-card__value">
                            {contract.build_cost}
                          </span>
                        </div>
                      )}
                      {contract.build_period && (
                        <div className="contract-summary-card__row">
                          <span className="contract-summary-card__label">구축기간</span>
                          <span className="contract-summary-card__value">
                            {contract.build_period}
                          </span>
                        </div>
                      )}
                      {contract.maintenance_fee && (
                        <div className="contract-summary-card__row">
                          <span className="contract-summary-card__label">월 유지보수비</span>
                          <span className="contract-summary-card__value">
                            {contract.maintenance_fee}
                          </span>
                        </div>
                      )}
                      {contract.maintenance_period && (
                        <div className="contract-summary-card__row">
                          <span className="contract-summary-card__label">유지보수기간</span>
                          <span className="contract-summary-card__value">
                            {contract.maintenance_period}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {contract.payment_terms && contract.payment_terms.length > 0 && (
                  <div className="contract-field contract-field--full">
                    <span className="contract-field__label">지불방법</span>
                    <div className="contract-payment-table-wrap">
                      <table className="contract-payment-table">
                        <thead>
                          <tr>
                            <th>구분</th>
                            <th>금액</th>
                            <th>지급조건</th>
                          </tr>
                        </thead>
                        <tbody>
                          {contract.payment_terms.map((row, index) => (
                            <tr key={index}>
                              <td>{row.category || '-'}</td>
                              <td>{row.amount || '-'}</td>
                              <td>{row.condition || '-'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                <div className="contract-field contract-field--full">
                  <span className="contract-field__label">고객(갑) 정보</span>
                  <div className="contract-summary-card">
                    <div className="contract-summary-card__row">
                      <span className="contract-summary-card__label">대표자</span>
                      <span className="contract-summary-card__value">
                        {contract.representative || contract.customer_name || '-'}
                      </span>
                    </div>
                    <div className="contract-summary-card__row">
                      <span className="contract-summary-card__label">상호명</span>
                      <span className="contract-summary-card__value">
                        {contract.company_name || '-'}
                      </span>
                    </div>
                    <div className="contract-summary-card__row">
                      <span className="contract-summary-card__label">사업자등록번호(주민번호)</span>
                      <span className="contract-summary-card__value">
                        {contract.business_number || '-'}
                      </span>
                    </div>
                    <div className="contract-summary-card__row">
                      <span className="contract-summary-card__label">이메일</span>
                      <span className="contract-summary-card__value">
                        {contract.email || '-'}
                      </span>
                    </div>
                    <div className="contract-summary-card__row">
                      <span className="contract-summary-card__label">주소</span>
                      <span className="contract-summary-card__value">
                        {contract.address || '-'}
                      </span>
                    </div>
                  </div>
                </div>

                {contract.provider_company_name && (
                  <div className="contract-field contract-field--full">
                    <span className="contract-field__label">공급자(을) 정보</span>
                    <div className="contract-summary-card">
                      <div className="contract-summary-card__row">
                        <span className="contract-summary-card__label">상호명</span>
                        <span className="contract-summary-card__value">
                          {contract.provider_company_name}
                        </span>
                      </div>
                      <div className="contract-summary-card__row">
                        <span className="contract-summary-card__label">대표자</span>
                        <span className="contract-summary-card__value">
                          {contract.provider_representative || '-'}
                        </span>
                      </div>
                      <div className="contract-summary-card__row">
                        <span className="contract-summary-card__label">사업자등록번호</span>
                        <span className="contract-summary-card__value">
                          {contract.provider_business_number || '-'}
                        </span>
                      </div>
                      <div className="contract-summary-card__row">
                        <span className="contract-summary-card__label">연락처</span>
                        <span className="contract-summary-card__value">
                          {contract.provider_contact || '-'}
                        </span>
                      </div>
                      <div className="contract-summary-card__row">
                        <span className="contract-summary-card__label">주소</span>
                        <span className="contract-summary-card__value">
                          {contract.provider_address || '-'}
                        </span>
                      </div>
                      <div className="contract-summary-card__row">
                        <span className="contract-summary-card__label">계좌번호</span>
                        <span className="contract-summary-card__value">
                          {contract.provider_account_number || '-'}
                        </span>
                      </div>
                    </div>
                  </div>
                )}

                <div className="contract-field contract-field--full">
                  <span className="contract-field__label">계약 약관</span>
                  <div className="contract-terms" tabIndex={0}>
                    <pre className="contract-terms__text">{contractTermsText}</pre>
                  </div>
                </div>

                {isSignedView ? (
                  <>
                    <div className="contract-signed-agreements">
                      <div className="contract-signed-agreements__item">계약 약관 동의 완료</div>
                      <div className="contract-signed-agreements__item">개인정보 수집 및 이용 동의 완료</div>
                    </div>

                    <div className="contract-field contract-field--full">
                      <span className="contract-field__label">고객 서명</span>
                      <div className="contract-signed-signature">
                        <div className="contract-signed-signature__meta">
                          <span>서명자: {signatureText || signerName || '-'}</span>
                          <span>서명일: {formatSignedAt(contract.signed_at)}</span>
                        </div>
                        <div className="contract-signed-signature__box">
                          {signatureImageSrc ? (
                            <img
                              src={signatureImageSrc}
                              alt={`${signatureText || signerName || '고객'} 서명`}
                              className="contract-signed-signature__image"
                            />
                          ) : (
                            <p className="contract-signed-signature__text">
                              {signatureText || signerName || '-'}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="contract-agree-list">
                  <label
                    className={`contract-checkbox${agree.termsAgreed ? ' contract-checkbox--checked' : ''}`}
                  >
                    <input
                      type="checkbox"
                      checked={agree.termsAgreed}
                      onChange={(e) => updateAgree('termsAgreed', e.target.checked)}
                    />
                    <span
                      className={`contract-checkbox__box${
                        agree.termsAgreed ? ' contract-checkbox__box--checked' : ''
                      }`}
                      aria-hidden="true"
                    >
                      {agree.termsAgreed && (
                        <svg className="contract-checkbox__icon" viewBox="0 0 12 10" aria-hidden="true">
                          <path
                            d="M1 5.5L4.5 9L11 1"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      )}
                    </span>
                    <span className="contract-checkbox__label">
                      계약 약관에 동의합니다. <span className="contract-field__required">*</span>
                    </span>
                  </label>
                  {agreeErrors.termsAgreed && (
                    <p className="contract-field__error">{agreeErrors.termsAgreed}</p>
                  )}

                  <label
                    className={`contract-checkbox${
                      agree.privacyAgreed ? ' contract-checkbox--checked' : ''
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={agree.privacyAgreed}
                      onChange={(e) => updateAgree('privacyAgreed', e.target.checked)}
                    />
                    <span
                      className={`contract-checkbox__box${
                        agree.privacyAgreed ? ' contract-checkbox__box--checked' : ''
                      }`}
                      aria-hidden="true"
                    >
                      {agree.privacyAgreed && (
                        <svg className="contract-checkbox__icon" viewBox="0 0 12 10" aria-hidden="true">
                          <path
                            d="M1 5.5L4.5 9L11 1"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      )}
                    </span>
                    <span className="contract-checkbox__label">
                      개인정보 수집 및 이용에 동의합니다.{' '}
                      <span className="contract-field__required">*</span>
                    </span>
                  </label>
                  {agreeErrors.privacyAgreed && (
                    <p className="contract-field__error">{agreeErrors.privacyAgreed}</p>
                  )}
                </div>
                )}
              </div>
            )}

            {step === 3 && contract && !isSignedView && (
              <div className="contract-panel">
                <h2 className="contract-panel__title">전자서명</h2>
                <p className="contract-panel__desc">
                  계약 내용을 확인하셨다면, 아래 버튼을 눌러 직접 서명해 주세요.
                </p>

                <div className="contract-summary-card">
                  <div className="contract-summary-card__row">
                    <span className="contract-summary-card__label">계약 유형</span>
                    <span className="contract-summary-card__value">
                      {getContractTypeLabel(String(contract.contract_type))}
                    </span>
                  </div>
                  <div className="contract-summary-card__row">
                    <span className="contract-summary-card__label">고객명</span>
                    <span className="contract-summary-card__value">
                      {contract.customer_name || '-'}
                    </span>
                  </div>
                  <div className="contract-summary-card__row">
                    <span className="contract-summary-card__label">대표자/담당자</span>
                    <span className="contract-summary-card__value">{signerName || '-'}</span>
                  </div>
                </div>

                <div className="contract-field contract-field--full">
                  <span className="contract-field__label">
                    서명 <span className="contract-field__required">*</span>
                  </span>
                  <div className="contract-signature-trigger">
                    {agree.signatureImage ? (
                      <div className="contract-signature-trigger__preview">
                        <img
                          className="contract-signature-trigger__image"
                          src={agree.signatureImage}
                          alt="입력된 서명"
                        />
                        <button
                          type="button"
                          className="contract-signature-trigger__rewrite"
                          onClick={() => setIsSignatureModalOpen(true)}
                        >
                          다시 서명
                        </button>
                      </div>
                    ) : (
                      <button
                        type="button"
                        className={`contract-signature-trigger__button${
                          agreeErrors.signatureImage
                            ? ' contract-signature-trigger__button--error'
                            : ''
                        }`}
                        onClick={() => setIsSignatureModalOpen(true)}
                      >
                        서명 입력하기
                      </button>
                    )}
                  </div>
                  <p className="contract-field__hint">
                    터치 또는 마우스로 직접 서명한 내용이 전자서명으로 기록됩니다.
                  </p>
                  {agreeErrors.signatureImage && (
                    <p className="contract-field__error">{agreeErrors.signatureImage}</p>
                  )}
                </div>
              </div>
            )}

            <div className="contract-actions">
              <button type="button" className="contract-actions__prev" onClick={goPrev}>
                ← 이전
              </button>

              {step < STEPS.length &&
              !isSignedView &&
              !(step === 2 && (needsMvpConfirm || needsPlanSelection)) ? (
                <button
                  type="button"
                  className="contract-actions__next"
                  onClick={goNext}
                  disabled={isLookingUp}
                >
                  {step === 1
                    ? isLookingUp
                      ? '조회 중...'
                      : '계약 불러오기 →'
                    : '다음 →'}
                </button>
              ) : (
                !isSignedView &&
                !(step === 2 && (needsMvpConfirm || needsPlanSelection)) && (
                  <button type="submit" className="contract-actions__next" disabled={isSubmitting}>
                    {isSubmitting ? '접수 중...' : '계약 완료하기 →'}
                  </button>
                )
              )}
            </div>
          </form>
        </div>
      </section>

      <SignaturePadModal
        open={isSignatureModalOpen}
        onClose={() => setIsSignatureModalOpen(false)}
        onConfirm={handleSignatureConfirm}
        stepLabel="3 / 3"
        initialDataUrl={agree.signatureImage || null}
      />

      <Footer />
    </div>
  );
};

export default Contract;
